import { addWeeks, formatCalendarWeek, startOfLocalWeek } from './dates';

export const DEFAULT_CALENDAR_WEEKS = 8;
export const MAX_CALENDAR_WEEKS = 12;
export const MAX_LOOKBACK_WEEKS = 12;

export type CapacityForecast = {
  thisWeekLabel: string;
  freeWeekLabel: string;
  freeHours: number;
  availableThisWeek: boolean;
};

export type WeekSlice = {
  start: Date;
  label: string;
  usedHours: number;
  freeHours: number;
  capacityHours: number;
};

function roundHours(hours: number): number {
  return Math.round(hours * 10) / 10;
}

/**
 * Burns open estimates at weekly capacity, Monday to Sunday.
 * The free week is the first calendar week that still has unused hours.
 */
export function forecastCapacity(
  allocatedHours: number,
  weeklyCapacityHours: number,
  today: Date,
  locale?: string,
): CapacityForecast {
  const allocated = assertAllocated(allocatedHours);
  const capacity = assertCapacity(weeklyCapacityHours);
  const weekIndex = Math.floor(allocated / capacity);
  const usedInFreeWeek = roundHours(allocated - weekIndex * capacity);
  const monday = startOfLocalWeek(today);
  return {
    thisWeekLabel: formatCalendarWeek(monday, locale),
    freeWeekLabel: formatCalendarWeek(addWeeks(monday, weekIndex), locale),
    freeHours: roundHours(capacity - usedInFreeWeek),
    availableThisWeek: weekIndex === 0,
  };
}

export function nextFreeCopy(forecast: CapacityForecast): string {
  if (forecast.availableThisWeek) {
    return `${forecast.freeHours}h free this week`;
  }
  return `Free ${forecast.freeWeekLabel} · ${forecast.freeHours}h`;
}

function assertAllocated(allocatedHours: number): number {
  if (typeof allocatedHours !== 'number' || !Number.isFinite(allocatedHours) || allocatedHours < 0) {
    throw new Error('Allocated hours must be a finite number of 0 or more.');
  }
  return roundHours(allocatedHours);
}

function assertCapacity(weeklyCapacityHours: number): number {
  if (
    typeof weeklyCapacityHours !== 'number'
    || !Number.isFinite(weeklyCapacityHours)
    || weeklyCapacityHours <= 0
  ) {
    throw new Error('Weekly capacity must be a positive number.');
  }
  return roundHours(weeklyCapacityHours);
}

/**
 * Burns open estimates left to right across Monday–Sunday weeks starting this week.
 */
export function weekSlices(
  allocatedHours: number,
  weeklyCapacityHours: number,
  today: Date,
  weekCount: number,
  locale?: string,
): WeekSlice[] {
  const allocated = assertAllocated(allocatedHours);
  const capacity = assertCapacity(weeklyCapacityHours);
  if (!Number.isInteger(weekCount) || weekCount < 1) {
    throw new Error('Week count must be an integer of 1 or more.');
  }
  const monday = startOfLocalWeek(today);
  let remaining = allocated;
  const slices: WeekSlice[] = [];
  for (let index = 0; index < weekCount; index += 1) {
    const usedHours = roundHours(Math.min(capacity, remaining));
    remaining = roundHours(Math.max(0, remaining - usedHours));
    const start = addWeeks(monday, index);
    slices.push({
      start,
      label: formatCalendarWeek(start, locale),
      usedHours,
      freeHours: roundHours(capacity - usedHours),
      capacityHours: capacity,
    });
  }
  return slices;
}

/** How many future weeks the default Team grid should show (8–12). */
export function visibleFutureWeekCount(
  people: Array<{ allocated_hours: number; weekly_capacity_hours: number }>,
): number {
  let farthest = 0;
  for (const person of people) {
    if (
      typeof person.allocated_hours !== 'number'
      || typeof person.weekly_capacity_hours !== 'number'
      || !Number.isFinite(person.allocated_hours)
      || !Number.isFinite(person.weekly_capacity_hours)
      || person.weekly_capacity_hours <= 0
    ) {
      continue;
    }
    const allocated = roundHours(person.allocated_hours);
    const capacity = roundHours(person.weekly_capacity_hours);
    const weekIndex = Math.floor(allocated / capacity);
    if (weekIndex > farthest) farthest = weekIndex;
  }
  return Math.min(MAX_CALENDAR_WEEKS, Math.max(DEFAULT_CALENDAR_WEEKS, farthest + 1));
}
